import { useState } from "react";
import { Input } from "@/components/ui/input";
import { X, Search as SearchIcon } from "lucide-react";

/**
 * Multi-value chip search for LRNs / reference numbers.
 *
 * The caller owns the committed set (`chips`) and re-runs its search whenever it changes —
 * the value sent to the backend is `chips.join(",")`, which the orders list endpoint splits
 * back into OR'd terms. Type a value + Enter (or comma) to add a chip; paste a comma-separated
 * list to add many at once; × removes one; Clear empties; Search commits any pending typed text.
 */
export function LrnChipSearch({ chips = [], onChange, maxChips = 25, placeholder }) {
  const [text, setText] = useState("");

  const addTerms = (raw) => {
    // Split on commas, spaces, tabs AND newlines — so pasting a column of LRNs (one per line),
    // or a space- or comma-separated list, all become separate chips.
    const parts = String(raw).split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...chips];
    for (const p of parts) {
      if (next.length >= maxChips) break;
      if (!next.includes(p)) next.push(p);
    }
    onChange(next.slice(0, maxChips));
  };

  const commitInput = () => {
    if (text.trim()) { addTerms(text); setText(""); }
  };

  const removeChip = (term) => onChange(chips.filter((c) => c !== term));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && !text && chips.length) {
      removeChip(chips[chips.length - 1]);
    }
  };

  const atMax = chips.length >= maxChips;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {chips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
              {c}
              <button
                type="button"
                onClick={() => removeChip(c)}
                className="text-slate-400 hover:text-slate-700"
                aria-label={`Remove ${c}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={(e) => {
          const t = e.clipboardData.getData("text");
          // Any comma/space/newline-separated paste -> split into chips (not one blob).
          if (/[\s,]/.test(t)) { e.preventDefault(); addTerms(t); }
        }}
        disabled={atMax}
        placeholder={atMax ? `Max ${maxChips} reached` : (placeholder || `Search upto ${maxChips} LRNs`)}
        className="border-0 shadow-none focus-visible:ring-0 px-0 h-8"
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-slate-400">
          {chips.length > 0 ? `${chips.length}/${maxChips} · press Enter to add` : ""}
        </span>
        <div className="flex items-center gap-4">
          {(chips.length > 0 || text) && (
            <button
              type="button"
              onClick={() => { setText(""); onChange([]); }}
              className="text-sm font-medium text-red-500 hover:text-red-600"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={commitInput}
            className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            <SearchIcon className="w-4 h-4" />
            Search
          </button>
        </div>
      </div>
    </div>
  );
}
